// Rows and cards for tasks, decisions and projects, shared by Desk, Projects and Decisions.
import { useState } from 'react';
import { Link } from 'react-router';
import { Ellipsis } from 'lucide-react';
import { api, errorText } from '../lib/api';
import { words } from '../lib/format';
import type { Decision, Project, Task } from '../lib/types';
import { Audience, Chip, Demo, Due, Field, Modal } from './ui';

// Mirrors TASK_TRANSITIONS in app/api/records.py (the server decides; this only shapes the menu).
const NEXT: Record<string, string[]> = {
  proposed: ['ready', 'cancelled'],
  ready: ['running', 'waiting_for_input', 'waiting_for_approval', 'completed', 'cancelled', 'proposed'],
  running: ['waiting_for_input', 'waiting_for_approval', 'completed', 'failed', 'cancelled', 'ready'],
  waiting_for_input: ['ready', 'running', 'completed', 'failed', 'cancelled'],
  waiting_for_approval: ['ready', 'running', 'completed', 'failed', 'cancelled'],
  failed: ['ready', 'cancelled'],
  completed: ['ready'],
  cancelled: ['proposed', 'ready'],
};

export function TaskRow({
  task,
  slug,
  onChanged,
  showWorkspace,
  highlight,
}: {
  task: Task;
  slug: string | null;
  onChanged?: (t: Task) => void;
  showWorkspace?: boolean;
  highlight?: boolean;
}) {
  const [verifying, setVerifying] = useState(false);
  const [evidence, setEvidence] = useState(task.verification ?? '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function move(state: string, verification?: string) {
    if (!slug) return;
    setBusy(true);
    setError('');
    try {
      const t = await api<Task>(`/api/w/${slug}/tasks/${task.id}/state`, { body: { state, verification } });
      onChanged?.(t);
      setVerifying(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className={`rec task${highlight ? ' highlight' : ''}`} id={`task-${task.id}`}>
      <div className="rec-main">
        <span className="rec-title">{task.title}</span>
        <div className="rec-meta">
          <Chip value={task.state} />
          <Due iso={task.due_date} />
          {task.owner && <span className="muted small">owner: {task.owner.name}</span>}
          {showWorkspace && task.workspace && <span className="muted small">{task.workspace}</span>}
          <Audience item={task} />
          <Demo show={task.demo} />
        </div>
        {task.state_note && <p className="muted small">{task.state_note}</p>}
        {task.state === 'completed' && task.verification && <p className="small verified">Checked: {task.verification}</p>}
        {error && (
          <p className="msg err small" role="alert">
            {error}
          </p>
        )}
      </div>
      {slug && onChanged && (
        <label className="rec-action">
          <span className="sr-only">Move “{task.title}” to</span>
          {/* phone: a round "more" button; the transparent select on top opens the phone's own picker */}
          <Ellipsis className="rec-action-icon" size={20} strokeWidth={1.75} aria-hidden="true" />
          <select
            value=""
            disabled={busy}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'completed') setVerifying(true);
              else if (v) void move(v);
            }}
          >
            <option value="">Move to…</option>
            {(NEXT[task.state] ?? []).map((s) => (
              <option key={s} value={s}>
                {words(s)}
              </option>
            ))}
          </select>
        </label>
      )}
      {verifying && (
        <Modal title="Mark as completed" onClose={() => setVerifying(false)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void move('completed', evidence);
            }}
          >
            <p className="muted">Before calling it done: how was the result checked? This stays on the task as evidence.</p>
            <Field label="Verification">
              <textarea required rows={3} value={evidence} onChange={(e) => setEvidence(e.target.value)} />
            </Field>
            {error && <p className="msg err">{error}</p>}
            <footer className="modal-foot">
              <button type="button" className="btn ghost" onClick={() => setVerifying(false)}>
                Cancel
              </button>
              <button type="submit" className="btn primary" disabled={busy || !evidence.trim()}>
                Mark completed
              </button>
            </footer>
          </form>
        </Modal>
      )}
    </li>
  );
}

export function DecisionRow({ d, slug, showWorkspace }: { d: Decision; slug: string | null; showWorkspace?: boolean }) {
  const target = slug ?? null;
  const title = target ? <Link to={`/w/${target}/decisions?open=${d.id}`}>{d.title}</Link> : d.title;
  return (
    <li className="rec decision">
      <div className="rec-main">
        <span className="rec-title">{title}</span>
        <div className="rec-meta">
          <Chip value={d.status} />
          <Due iso={d.due_date} />
          {showWorkspace && d.workspace && <span className="muted small">{d.workspace}</span>}
          <Audience item={d} />
          <Demo show={d.demo} />
        </div>
        {d.recommendation && <p className="small">Recommendation: {d.recommendation}</p>}
      </div>
    </li>
  );
}

export function ProjectCard({ p, slug }: { p: Project; slug: string }) {
  return (
    <li className="card project-card">
      <Link to={`/w/${slug}/projects/${p.id}`} className="card-link">
        <div className="row gap wrap">
          <Chip value={p.stage} />
          <Audience item={p} />
          <Demo show={p.demo} />
        </div>
        <h3>{p.title}</h3>
        {p.goal && <p className="muted small clamp">{p.goal}</p>}
        <div className="row gap wrap small muted">
          {p.owner && <span>owner: {p.owner.name}</span>}
          {p.capacity_hours_per_week !== null && <span>{p.capacity_hours_per_week} h/week</span>}
          {p.tasks && (
            <span>
              {p.tasks.open} open · {p.tasks.done} done
            </span>
          )}
        </div>
      </Link>
    </li>
  );
}

/** workspace slug of a record shown on a cross-workspace page (the personal desk) */
export function slugFor(workspaces: { name: string; slug: string }[], name: string | null | undefined): string | null {
  return workspaces.find((w) => w.name === name)?.slug ?? null;
}
